/* eslint-disable compat/compat */

import { get } from 'svelte/store';
import {
  config,
  searchCursor,
  results,
  facetValues,
  searching,
  totalCount,
  searchTerm,
  searchPage,
  errorMessage,
} from './store.js';
import { SvelteURLSearchParams } from 'svelte/reactivity';

const state = {
  config: get(config),
  facetValues: [],
  searchCursor: {},
  searchTerm: '',
  searchPage: undefined,
  results: [],
};

searchCursor.subscribe((value) => {
  state.searchCursor = value;
});
facetValues.subscribe((value) => {
  state.facetValues = value;
});
searchTerm.subscribe((value) => {
  state.searchTerm = value;
});
searchPage.subscribe((value) => {
  state.searchPage = value;
});
results.subscribe((value) => {
  state.results = value;
});

function loadFacets(facets) {
  const newValues = facets.map((facet) => {
    const oldValue = state.facetValues.find((f) => f.id === facet.id);
    const facetConfig = state.config.facets.find((f) => f.id === facet.id);

    const index = state.config.facets.indexOf(facetConfig);
    return {
      ...facet,
      values: parseItems(facet.values),
      selectedValues: oldValue?.selectedValues || [],
      config: facetConfig,
      index,
    };
  });
  newValues.sort((a, b) => a.index - b.index);
  facetValues.set(newValues);
}

function parseAllFacetValues(facet, config) {
  const facetList = facet.values;

  const parsedList = parseItems(facetList);

  const finalList = [];

  for (const entry of parsedList) {
    const item = {
      ...entry,
    };

    // Overwrite the value's name with a field specified by "Label Field Name"
    if (config.labelFieldName) {
      const fieldNames = config.labelFieldName.split(',').map((f) => f.trim());
      for (const name of fieldNames) {
        if (item[name]) {
          item.name = item[name];
          item.Name = item[name];
          break;
        }
      }
    }
    finalList.push(item);
  }

  return finalList;
}

function parseItems(items) {
  return items.map((i) => {
    // GraphQL returns items as { item: {} }
    const item = i?.item || i;
    const obj = {
      ...i,
      ...item,
    };

    if (item.parent) {
      obj.parent = parseItems([item.parent])[0];
    }

    if (obj.facets) {
      for (const key of Object.keys(obj.facets)) {
        if (obj.facets[key] && typeof obj.facets[key] === 'string') {
          if (obj.facets[key].startsWith('[')) {
            obj.facets[key] = JSON.parse(obj.facets[key]);
          } else {
            obj.facets[key] = [obj.facets[key]];
          }
        }
      }
    }

    return obj;
  });
}

export function populateFirstSearchFacets(forceUpdate = false) {
  const queries = new SvelteURLSearchParams(window.location.search);
  state.facetValues = Object.keys(state.config.queryFacets).map((facet) => {
    let values = [];

    let queryValue = queries.get(facet);
    let requireAll = true;
    if (queryValue) {
      if (queryValue.startsWith('~')) {
        requireAll = false;
        queryValue = queryValue.substring(1);
      }
      values = queryValue.split('|');
    }

    return {
      name: facet,
      id: facet,
      selectedValues: values,
      config: {
        requireAll,
      },
    };
  });

  if (queries.has('keyword')) {
    searchTerm.set(queries.get('keyword'));
  }

  if ((!state.searchPage && queries.has('page')) || forceUpdate) {
    searchPage.set(Number(queries.get('page')));
  }
}

function persistFacets() {
  const queries = new SvelteURLSearchParams();
  for (const facet of state.facetValues) {
    let value = false;
    if (Array.isArray(facet.selectedValues)) {
      if (facet.selectedValues.length > 0) {
        value = facet.selectedValues.join('|');
      }
    } else if (facet.selectedValues) {
      value = facet.selectedValues;
    }

    if (value) {
      if (!facet.config.requireAll) {
        value = '~' + value;
      }
      queries.append(facet.id, value);
    }
  }

  if (state.searchTerm) {
    queries.append('keyword', state.searchTerm);
  }
  if (state.searchPage && state.searchPage !== 1) {
    queries.append('page', state.searchPage);
  } else {
    queries.delete('page');
  }

  const backupState = {
    ...state,
  };
  delete backupState.config;

  history.pushState(queries.toString(), '', '?' + queries.toString());
}

function sleep(time = 500) {
  return new Promise((res) => setTimeout(res, time));
}
export async function performSearchEvent(e) {
  e.preventDefault();

  return await performSearch();
}

function createQuery(baseUrl, options) {
  let firstOption = true;
  let url = baseUrl;
  for (const [key, value] of Object.entries(options)) {
    url += (firstOption ? '?' : '&') + key + '=' + encodeURIComponent(value);

    firstOption = false;
  }

  return url;
}

export async function safeRequest(options, attempts = 0) {
  try {
    const res = await fetch(
      createQuery(state.config.origin + state.config.endpoint, options)
    );

    if (res.status !== 200) {
      if (res.status === 429) {
        throw new Error(window.kbSearchServiceManager.strings.ERROR_RATELIMIT);
      }
      throw new Error(window.kbSearchServiceManager.strings.ERROR_GENERIC);
    }
    return res;
  } catch (err) {
    if (attempts >= 3) {
      throw err;
    } else {
      await sleep((attempts + 1) * 3000);
      return await safeRequest(options, attempts + 1);
    }
  }
}

export async function performSearch(loadMore = false, shouldPersistFacets = true) {
  searching.set(true);

  try {
    const startTime = Date.now();

    if (typeof loadMore === 'number') {
      searchPage.set(loadMore);
      state.searchCursor.endCursor = 0;
      loadMore = false;
    } else if (loadMore === true) {
      searchPage.set(undefined);
    } else {
      state.searchCursor.endCursor = 0;
    }

    if (state.facetValues.length === 0) {
      populateFirstSearchFacets();
    } else if (shouldPersistFacets) {
      persistFacets();
    }

    if (!state.searchPage) {
      searchPage.set(1);
    }

    const fieldsContains = [];
    const fieldsEquals = [];
    for (const facet of state.facetValues) {
      let container = fieldsEquals;
      if (!facet.config.requireAll) {
        container = [];
      }
      if (Array.isArray(facet.selectedValues)) {
        for (const value of facet.selectedValues) {
          container.push({
            name: facet.id,
            values: [value],
          });
        }
      } else {
        if (facet.selectedValues) {
          container.push({
            name: facet.id,
            values: [facet.selectedValues],
          });
        }
      }

      if (!facet.config.requireAll && container.length > 0) {
        fieldsEquals.push({
          name: facet.id,
          values: container.map((v) => v.values[0]),
        });
      }
    }

    if (state.config.searchField) {
      fieldsContains.push({
        name: state.config.searchField,
        values: [state.searchTerm],
      });
    }

    const searchConfig = {
      facets: Object.keys(state.config.queryFacets),
      rootNode: state.config.rootNode ?? -1,
      rootCategory: state.config.rootCategory,
      allowedTemplates: state.config.allowedTemplates,
      cursor: state.searchCursor.endCursor,
      fieldsContains: JSON.stringify(fieldsContains),
      fieldsEquals: JSON.stringify(fieldsEquals),
      sortBy: state.config.sortField?.map((f) => f.trim()) ?? null,
      sortDesc: state.config.sortDescending ?? false,
      q: state.config.searchField ? null : state.searchTerm,
      count: state.config.count ?? 16,
      boostId: state.config.boostId ?? -1,
    };

    // console.debug('Search config:', searchConfig);

    const res = await safeRequest(searchConfig);
    const body = await res.json();

    const diffTime = Date.now() - startTime;
    const remainingTime = 200 - diffTime;
    if (remainingTime > 0) {
      await sleep(remainingTime);
    }

    if (body.facets) {
      const facets = body.facets.map((facet) => {
        const config = {
          id: facet.id,
          name: state.config.queryFacets[facet.id].label,
          type: state.config.queryFacets[facet.id].type,
          requireAll: facet.requireAll === '1',
          facet: facet.id,
          primary: facet.primaryFacet === '1',
          showCount: facet.showCount === '1',
          sortByCount: facet.sortByCount === '1',
          labelFieldName: facet.labelFieldName || undefined,
          placeholderText: facet.placeholderText,
          values: [],
        };
        config.values = parseAllFacetValues(facet, config);
        return config;
      });

      state.config.facets = facets;
      // state.config.template = body.data.registry.template.value;
      config.set(state.config);
    }

    const search = body;

    let items = parseItems(search.results.items);

    totalCount.set(search.results.totalCount);

    if (loadMore === true) {
      results.set([...state.results, ...items]);
    } else {
      results.set(items);
    }

    searchCursor.set(search.results.pageInfo);
    errorMessage.set(null);
    loadFacets(search.facets);
  } catch (err) {
    results.set([]);
    errorMessage.set(err.message);
    console.error(err);
  } finally {
    searching.set(false);
  }
}
